import { Close } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
} from "@mui/material";
import { AxiosError } from "axios";
import MButton from "components/momentum/MButton";
import {
  CSEAssessmentStep,
  CustomerDetailStep,
  EpsmPartnerAssessmentStep,
  PartnerAssessmentStep,
  VidcastAssessmentStep,
  createSuccessPlanAPI,
  getStepIndicator,
  useSpContext,
} from "components/summary/success-plan";
import SuccessPlanLayout from "components/summary/success-plan/SuccessPlanLayout";
import SuccessPlanHeader from "components/summary/success-plan/components/SuccessPlanHeader";
import SuccessPlanProfile from "components/summary/success-plan/components/SuccessPlanProfile";
import CustomerAssessmentStep from "components/summary/success-plan/components/workflow/CustomerAssessmentStep";
import WorkloadAssessmentStep from "components/summary/success-plan/components/workflow/WorkloadAssessmentStep";
import { csmValidateFields, relationshipLevels } from "components/summary/success-plan/constants";
import {
  SuccessPlanFormFields,
  SuccessPlanState,
} from "components/summary/success-plan/state/successPlan.types";
import moment from "moment";
import { useAxios } from "myAxios";
import { AuthProvider } from "pages";
import { ReactNode, useContext, useState } from "react";
import { UseFormReturn, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import styles from "./SuccessPlanWorkflow.module.scss";

type SuccessPlanEditWorkflowProps = {
  open: boolean;
  onClose: () => void;
  spState: SuccessPlanState;
};

type StepperItem = {
  step: number;
  label: string;
  content: (state: SuccessPlanState, form: UseFormReturn<any>) => ReactNode;
};

const CSMSteppers: StepperItem[] = [
  {
    step: 0,
    label: "Customer Assessment",
    content: (state: SuccessPlanState, form: UseFormReturn<any>) => (
      <CustomerAssessmentStep
        spState={state}
        form={form}
      />
    ),
  },
  {
    step: 1,
    label: "Workload Assessment",
    content: (state: SuccessPlanState, form: UseFormReturn<any>) => (
      <WorkloadAssessmentStep
        spState={state}
        form={form}
      />
    ),
  },
  {
    step: 2,
    label: "Customer Details",
    content: (state: SuccessPlanState, form: UseFormReturn<any>) => (
      <CustomerDetailStep
        spState={state}
        form={form}
      />
    ),
  },
];

const CSESteppers: StepperItem[] = [
  {
    step: 0,
    label: "CSE Assessment",
    content: (state: SuccessPlanState, form: UseFormReturn<any>) => (
      <CSEAssessmentStep
        spState={state}
        form={form}
      />
    ),
  },
];

const PartnerSteppers: StepperItem[] = [
  {
    step: 0,
    label: "PSM Partner Assessment",
    content: (state: SuccessPlanState, form: UseFormReturn<any>) => (
      <PartnerAssessmentStep
        spState={state}
        form={form}
      />
    ),
  },
];

const EpsmPartnerSteppers: StepperItem[] = [
  {
    step: 0,
    label: "EPSM Partner Assessment",
    content: (state: SuccessPlanState, form: UseFormReturn<any>) => (
      <EpsmPartnerAssessmentStep
        spState={state}
        form={form}
      />
    ),
  },
];

const VidcastSteppers: StepperItem[] = [
  {
    step: 0,
    label: "Vidcast Assessment",
    content: (state: SuccessPlanState, form: UseFormReturn<any>) => (
      <VidcastAssessmentStep
        spState={state}
        form={form}
      />
    ),
  },
];

const DetailSteppers: StepperItem[] = [
  {
    step: 0,
    label: "Customer Details",
    content: (state: SuccessPlanState, form: UseFormReturn<any>) => (
      <CustomerDetailStep
        spState={state}
        form={form}
      />
    ),
  },
];

function getStepper(stepIndicator: SuccessPlanState["stepIndicator"]): StepperItem[] {
  if (stepIndicator === "csmStepper") {
    return CSMSteppers;
  }
  if (stepIndicator === "cseStepper") {
    return CSESteppers;
  }
  if (stepIndicator === "partnerStepper") {
    return PartnerSteppers;
  }
  if (stepIndicator === "epsmPartnerStepper") {
    return EpsmPartnerSteppers;
  }
  if (stepIndicator === "vidcastStepper") {
    return VidcastSteppers;
  }
  if (stepIndicator === "customerDetailStepper") {
    return DetailSteppers;
  }
  return [];
}

function getPayload(
  stepIndicator: SuccessPlanState["stepIndicator"],
  data: any
): Partial<SuccessPlanFormFields> | null {
  if (stepIndicator === "csmStepper") {
    const cc = data.competitorContacts;
    const competitorContactResult = processContacts(cc || []);
    return {
      ...data,
      competitorContacts: competitorContactResult,
    };
  }
  if (stepIndicator === "cseStepper") {
    return {
      accountKeys: data.accountKeys,
      ccseRisk: data.ccseRisk,
      ccseRiskNote: data.ccseRiskNote,
    };
  }
  if (stepIndicator === "partnerStepper") {
    return {
      accountKeys: data.accountKeys,
      partnerTeamRisk: data.partnerTeamRisk,
      partnerTeamRiskNote: data.partnerTeamRiskNote,
    };
  }
  if (stepIndicator === "epsmPartnerStepper") {
    return {
      accountKeys: data.accountKeys,
      epsmRisk: data.epsmRisk,
      epsmRiskNote: data.epsmRiskNote,
    };
  }
  if (stepIndicator === "vidcastStepper") {
    return {
      accountKeys: data.accountKeys,
      vidCsmRisk: data.vidCsmRisk,
      vidCsmRiskNote: data.vidCsmRiskNote,
    };
  }
  if (stepIndicator === "customerDetailStepper") {
    const cc = data.competitorContacts;
    const competitorContactResult = processContacts(cc || []);

    return {
      accountKeys: data.accountKeys,
      hasMsftContract: data.hasMsftContract,
      contractType: data.contractType || "",
      relationships: data.relationships,
      relationshipOtherContent: data.relationshipOtherContent,
      msftRenewalDate: data.msftRenewalDate,
      knowDecMaker: data.knowDecMaker,
      competitorContacts: competitorContactResult,
      recentExecutive: data.recentExecutive,
      responsiveLvl: data.responsiveLvl,
    };
  }
  return null;
}

function processContacts(contacts: any[]) {
  const competitorContactResult = contacts
    .filter(
      (c: any) => c.decMakerName !== "" || c.decMakerTitle !== "" || c.decMakerSentiment !== ""
    )
    .map((c: any) => {
      const devices = c.competitorDevices;
      const deviceOtherContent = c.competitorDevicesOtherContent;
      const mtg = c.competitorMtg;
      const mtgOtherContent = c.competitorMtgOtherContent;
      const calling = c.competitorCalling;
      const callingOtherContent = c.competitorCallingOtherContent;

      const competitorDevices = devices.map((v: string) =>
        v === "Other" && deviceOtherContent !== "" ? `${v}%-%${deviceOtherContent}` : v
      );
      const competitorMtg = mtg.map((v: string) =>
        v === "Other" && mtgOtherContent !== "" ? `${v}%-%${mtgOtherContent}` : v
      );
      const competitorCalling = calling.map((v: string) =>
        v === "Other" && callingOtherContent !== "" ? `${v}%-%${callingOtherContent}` : v
      );

      return {
        decMakerName: c.decMakerName,
        decMakerTitle: c.decMakerTitle,
        decMakerSentiment: c.decMakerSentiment,
        competitorFlag: c.competitorFlag,
        competitorDevices,
        competitorMtg,
        competitorCalling,
      };
    });
  return competitorContactResult;
}

function seperateOther(value: string) {
  if (value.includes("%-%") && value.startsWith("Other")) {
    const parts = value.split("%-%");
    return {
      option: parts[0],
      content: parts[1] || "",
    };
  }
  return {
    option: value,
    content: "",
  };
}

function setContacts(contacts: any[]) {
  return contacts.map((c: any) => {
    const devices = c.competitorDevices || [];
    const deviceOtherContent = devices.find(
      (v: string) => v.includes("%-%") && v.startsWith("Other")
    )
      ? devices.find((v: string) => v.includes("%-%") && v.startsWith("Other"))?.split("%-%")[1] ||
        ""
      : "";
    const mtg = c.competitorMtg || [];
    const mtgOtherContent = mtg.find((v: string) => v.includes("%-%") && v.startsWith("Other"))
      ? mtg.find((v: string) => v.includes("%-%") && v.startsWith("Other"))?.split("%-%")[1] || ""
      : "";

    const calling = c.competitorCalling || [];
    const callingOtherContent = calling.find(
      (v: string) => v.includes("%-%") && v.startsWith("Other")
    )
      ? calling.find((v: string) => v.includes("%-%") && v.startsWith("Other"))?.split("%-%")[1] ||
        ""
      : "";

    return {
      ...c,
      //competitorFlag: String(c.competitorFlag || "0"),
      competitorDevices: devices.map((v: string) =>
        v.includes("%-%") && v.startsWith("Other") ? `${v.split("%-%")[0]}` : v
      ),
      competitorDevicesOtherContent: deviceOtherContent,
      competitorMtg: mtg.map((v: string) =>
        v.includes("%-%") && v.startsWith("Other") ? `${v.split("%-%")[0]}` : v
      ),
      competitorMtgOtherContent: mtgOtherContent,
      competitorCalling: calling.map((v: string) =>
        v.includes("%-%") && v.startsWith("Other") ? `${v.split("%-%")[0]}` : v
      ),
      competitorCallingOtherContent: callingOtherContent,
    };
  });
}

const SuccessPlanEditWorkflow = ({ open, onClose, spState }: SuccessPlanEditWorkflowProps) => {
  const { features } = useContext(AuthProvider);
  const { dispatch } = useSpContext();

  const { spData, account, stepIndicator } = spState;
  const { customerRisk, deviceRisk, meetingRisk, callingRisk, contactCenterRisk } = spData;
  const accountId = account?.accountId || "";

  const msftRenewalDate = spData.msftRenewalDate
    ? moment(spData.msftRenewalDate).format("MM/DD/YYYY")
    : "";

  const relationshipPairs = (spData.relationships ?? []).map((item: string) => seperateOther(item));
  /*   const competitorMtgPairs = (spData.competitorMtg ?? []).map((item: string) =>
    seperateOther(item),
  );
  const competitorDevicesPairs = (spData.competitorDevices ?? []).map(
    (item: string) => seperateOther(item),
  ); */

  const relationshipOptions = relationshipLevels.filter((level) =>
    relationshipPairs.some((pair: any) => pair.option === level.value)
  );

  const form = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
    shouldUnregister: false,
    defaultValues: {
      customerRisk: customerRisk || "",
      deRiskStrategies: spData.deRiskStrategies || [],
      customerRiskNote: "",
      certifyFlag: 0,
      escalationFlag: String(spData.escalationFlag ?? "0"),
      significantDoing: "",
      escalationNote: "",
      deviceRisk: deviceRisk || "",
      deviceRiskNote: "",
      meetingRisk: meetingRisk || "",
      meetingRiskNote: "",
      callingRisk: callingRisk || "",
      callingRiskNote: "",
      contactCenterRisk: contactCenterRisk || "",
      contactCenterRiskNote: "",
      ccseRisk: spData.ccseRisk || "",
      ccseRiskNote: "",
      partnerTeamRisk: spData.partnerTeamRisk || "",
      partnerTeamRiskNote: "",
      epsmRisk: spData.epsmRisk || "",
      epsmRiskNote: "",
      vidCsmRisk: spData.vidCsmRisk || "",
      vidCsmRiskNote: "",
      highTouchSegment: spData.highTouchSegment || "",
      hasMsftContract: String(spData.hasMsftContract ?? "0"),
      contractType: spData.contractType || "",
      msftRenewalDate: msftRenewalDate,
      relationships: relationshipOptions,
      relationshipOtherContent:
        relationshipPairs.find((pair: any) => pair.option === "Other")?.content || "",
      knowDecMaker: String(spData.knowDecMaker ?? "0"),
      competitorContacts: setContacts(spData.competitorContacts || []),
      recentExecutive: spData.recentExecutive || "",
      //responsiveLvl: spData.responsiveLvl || "",
    },
  });

  const { handleSubmit, trigger } = form;
  const [activeStep, setActiveStep] = useState<number>(0);
  const axios = useAxios();
  const spApi = createSuccessPlanAPI(axios);

  const steppers = getStepper(stepIndicator) as StepperItem[];
  if (steppers.length === 0) {
    toast.error("You don't have access to edit the success plan.", {
      position: "top-center",
      autoClose: 3000,
    });
    onClose();
    return null;
  }

  const handleStepChange = async (direction: number) => {
    const newStep = activeStep + direction;
    if (newStep >= 0 && newStep < steppers.length) {
      setActiveStep(newStep);
    } else if (newStep === steppers.length) {
      onClose();
    }
  };

  const handleNext = async () => {
    const fields = csmValidateFields[activeStep];
    const isValid = await trigger(fields as any);
    if (!isValid) {
      return;
    }

    handleStepChange(1);
  };

  const handlePrevious = () => {
    handleStepChange(-1);
  };

  const lastStep = activeStep === steppers.length - 1;
  const firstStep = activeStep === 0;

  const onSubmit = async (data: any) => {
    const selectedAccountKeys = spState.selectedAccounts.map((acc) => acc.accountKey);
    const relationships = (data.relationships ?? []).map((v: any) =>
      v.value === "Other" ? `${v.value}%-%${data.relationshipOtherContent}` : v.value
    );

    const msftRenewalDate =
      data.msftRenewalDate === "" ? "" : moment(data.msftRenewalDate).format("YYYY-MM-DD");

    const payload = getPayload(stepIndicator, {
      accountKeys: selectedAccountKeys,
      ...data,
      msftRenewalDate,
      relationships,
    });

    try {
      dispatch({ type: "request/pending" });
      if (spData.id) {
        await spApi.patchSuccessPlan(spData.id, payload);
      } else {
        await spApi.createSuccessPlan(payload);
      }
      const result = await spApi.getSuccessPlan(accountId);
      dispatch({ type: "request/success" });
      dispatch({ type: "successplan/refresh", payload: result });
      dispatch({
        type: "setStepIndicator",
        payload: getStepIndicator(features),
      });
      toast.success("Success Plan updated", {
        position: "top-center",
        autoClose: 3000,
      });
      onClose();
    } catch (e) {
      dispatch({ type: "request/failure" });
      toast.error(
        (e as AxiosError<{ message: string }>)?.response?.data?.message ||
          "Fail to update success plan",
        {
          position: "top-center",
          autoClose: 3000,
        }
      );
    }
  };

  const onError = (errors: any) => {
    console.log("Form Errors: ", errors);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      disableEnforceFocus
    >
      <DialogTitle className={styles["workflow-header"]}>
        <Stepper
          className={styles["workflow-steps"]}
          activeStep={activeStep}
        >
          {steppers.map((step) => (
            <Step key={step.step}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <div style={{ display: "flex", alignItems: "center" }}>
          <IconButton
            size={"small"}
            onClick={onClose}
          >
            <Close />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent style={{ marginTop: "8px" }}>
        <SuccessPlanLayout
          header={
            <SuccessPlanHeader
              mutateEpRevamp={() => Promise.resolve()}
              spState={spState}
              compact
            />
          }
          profile={<SuccessPlanProfile spState={spState} />}
          sectionGroup={
            <div
              style={{
                height: "100%",
                overflowY: "auto",
              }}
            >
              {steppers[activeStep].content(spState, form)}
            </div>
          }
        />
      </DialogContent>
      <DialogActions>
        <div>
          <MButton onClick={onClose}>Cancel</MButton>
          {!firstStep && (
            <MButton
              buttonType="blue"
              onClick={handlePrevious}
            >
              Previous
            </MButton>
          )}
          {lastStep ? (
            <MButton
              buttonType="blue"
              onClick={handleSubmit(onSubmit, onError)}
            >
              Save
            </MButton>
          ) : (
            <MButton
              buttonType="blue"
              onClick={handleNext}
            >
              Next
            </MButton>
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default SuccessPlanEditWorkflow;
